import ProactiveChatApi from 'dashboard/api/proactiveChat';

const state = {
  onlineContacts: [],
  onlineContactIds: [],
  isOnlineContactsWatcherActive: false,
  hasPendingOnlineContactsRefresh: false,
  uiFlags: {
    isFetching: false,
    isSending: false,
  },
  autoGreetingEnabled: false,
};

const getters = {
  getOnlineContacts: $state => $state.onlineContacts,
  getUIFlags: $state => $state.uiFlags,
  isAutoGreetingEnabled: $state => $state.autoGreetingEnabled,
};

const mutations = {
  SET_ONLINE_CONTACTS($state, contacts) {
    $state.onlineContacts = contacts;
  },
  SET_ONLINE_CONTACT_IDS($state, contactIds) {
    $state.onlineContactIds = contactIds;
  },
  SET_ONLINE_CONTACTS_WATCHER_ACTIVE($state, isActive) {
    $state.isOnlineContactsWatcherActive = isActive;
  },
  SET_PENDING_ONLINE_CONTACTS_REFRESH($state, isPending) {
    $state.hasPendingOnlineContactsRefresh = isPending;
  },
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  SET_AUTO_GREETING_ENABLED($state, enabled) {
    $state.autoGreetingEnabled = enabled;
  },
};

const actions = {
  async fetchOnlineContacts({ state: $state, commit, dispatch }) {
    if ($state.uiFlags.isFetching) {
      commit('SET_PENDING_ONLINE_CONTACTS_REFRESH', true);
      return;
    }

    commit('SET_PENDING_ONLINE_CONTACTS_REFRESH', false);
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await ProactiveChatApi.getOnlineContacts();
      commit('SET_ONLINE_CONTACTS', data.contacts || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('fetchOnlineContacts error', error);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });

      if (
        $state.isOnlineContactsWatcherActive &&
        $state.hasPendingOnlineContactsRefresh
      ) {
        commit('SET_PENDING_ONLINE_CONTACTS_REFRESH', false);
        await dispatch('fetchOnlineContacts');
      }
    }
  },

  updateOnlineContactIds({ state: $state, commit, dispatch }, contacts = {}) {
    const contactIds = Object.keys(contacts).sort();
    const hasChanged =
      contactIds.length !== $state.onlineContactIds.length ||
      contactIds.some((id, index) => id !== $state.onlineContactIds[index]);

    if (!hasChanged) return;

    commit('SET_ONLINE_CONTACT_IDS', contactIds);
    if ($state.isOnlineContactsWatcherActive) {
      dispatch('fetchOnlineContacts');
    }
  },

  startOnlineContactsWatcher({ state: $state, commit, dispatch }) {
    if ($state.isOnlineContactsWatcherActive) return;

    commit('SET_ONLINE_CONTACTS_WATCHER_ACTIVE', true);
    dispatch('fetchOnlineContacts');
  },

  stopOnlineContactsWatcher({ commit }) {
    commit('SET_ONLINE_CONTACTS_WATCHER_ACTIVE', false);
    commit('SET_PENDING_ONLINE_CONTACTS_REFRESH', false);
  },

  async sendInvitation({ commit }, { contactInboxId, message }) {
    commit('SET_UI_FLAG', { isSending: true });
    try {
      await ProactiveChatApi.sendInvitation({ contactInboxId, message });
      return { success: true };
    } catch (error) {
      return { success: false };
    } finally {
      commit('SET_UI_FLAG', { isSending: false });
    }
  },

  async inviteAll({ commit }, { message } = {}) {
    commit('SET_UI_FLAG', { isSending: true });
    try {
      const { data } = await ProactiveChatApi.inviteAll({ message });
      return { success: true, queued: data.queued, count: data.count };
    } catch (error) {
      return { success: false };
    } finally {
      commit('SET_UI_FLAG', { isSending: false });
    }
  },

  setAutoGreetingEnabled({ commit }, enabled) {
    commit('SET_AUTO_GREETING_ENABLED', enabled);
    ProactiveChatApi.toggleAutoGreeting(enabled);
  },

  async fetchAutoGreetingStatus({ commit }) {
    try {
      const { data } = await ProactiveChatApi.getAutoGreetingStatus();
      commit('SET_AUTO_GREETING_ENABLED', data.enabled);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('fetchAutoGreetingStatus error', error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
